import { __ } from "@wordpress/i18n";
import { useEffect, useMemo, useRef, useState } from "@wordpress/element";
import { Button } from "../../components/button";
import { Section } from "../../components/section";
import { OutlineObjectType, useDoc } from "./context";
import { Spinner } from "@wordpress/components";
import { getCaretOffset, setCaretAtOffset } from "../../utils/caret";
import { FaTrash } from "react-icons/fa";
import { FaPlus } from "react-icons/fa6";
import { toast } from "react-toastify";
import { css } from "@/utils/css";
import { api } from "@/config/api-fetch";

export function DocOutline() {
    const { preferences, outline, setOutline, addLessonAfter, removeLesson } = useDoc();
    const [processing, setProcessing] = useState(false);

    const { processed, total } = useMemo(() => {
        let processed = 0;
        let total = 0;

        if (outline) {
            total += 1;
            processed += outline.url ? 1 : 0;

            if (outline.lessons) {
                for (const lesson of outline.lessons) {
                    total += 1;
                    processed += lesson.url ? 1 : 0;

                    if (lesson.sub_lessons) {
                        for (const subLesson of lesson.sub_lessons) {
                            total += 1;
                            processed += subLesson.url ? 1 : 0;
                        }
                    }
                }
            }
        }

        return { processed, total };
    }, [outline]);

    if (!outline) return null;

    const handleContentGeneration = async (object: OutlineObjectType, parent_post_id?: number) => {
        if (!object.id || object.url) return;

        object.loading = true;
        setOutline({ ...outline });

        try {
            const payload: Record<string, any> = {
                doc_outline: outline,
                object_id: object.id,
                parent_post_id,
                tone: preferences?.tone,
                status: preferences?.status,
            };

            const formData = new FormData();

            preferences?.files?.forEach((file, index) => {
                formData.append(`files[${index}]`, file);
            });

            for (const key in payload) {
                let value = payload[key];

                if (typeof value === "object") value = JSON.stringify(value);

                formData.append(key, value);
            }

            const generated = await api.post<{
                content?: string;
                url?: string;
                post_id?: number;
            }>("/generate-doc", formData);

            object.url = generated.url || "";
            object.post_id = generated.post_id || 0;
        } catch (err: any) {
            toast.error(err?.message || __("An error occurred while generating content. Please try again.", "antimanual"));
        }

        object.loading = false;
        setOutline({ ...outline });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setProcessing(true);

        await handleContentGeneration(outline);

        for (const lesson of outline.lessons || []) {
            await handleContentGeneration(lesson, outline.post_id);

            for (const subLesson of lesson.sub_lessons || []) {
                await handleContentGeneration(subLesson, lesson.post_id);
            }
        }

        setProcessing(false);
    };

    return (
        <Section className="atml-doc-outline">
            <form onSubmit={handleSubmit}>
                <div className="atml-form-inner-wrapper">
                    <h2 className="atml-doc-main-title">
                        <LessonObjectTitle object={outline}>{outline.title}</LessonObjectTitle>
                    </h2>
                    {outline.lessons?.map((lesson) => {
                        return (
                            <div key={lesson.id} className="atml-lesson">
                                <div className="atml-lesson-actions">
                                    <div className="action remove-lesson" onClick={() => removeLesson(lesson.id)}>
                                        <FaTrash />
                                    </div>
                                    <div className="action add-lesson" onClick={() => addLessonAfter(lesson.id, "", "lesson")}>
                                        <FaPlus />
                                    </div>
                                </div>
                                <h3 className="atml-lesson-title">
                                    <LessonObjectTitle object={lesson}>{lesson.title}</LessonObjectTitle>
                                </h3>

                                <ul className="atml-sub-lessons">
                                    {lesson.sub_lessons?.map((subLesson) => (
                                        <li key={subLesson.id}>
                                            <LessonObjectTitle object={subLesson}>{subLesson.title}</LessonObjectTitle>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        );
                    })}

                    <div className={css(total === processed ? "hidden" : "flex-center")}>
                        <Button loading={processing} className={css("mt-12 p-3 min-w-sm")}>
                            {processed === 0 ? __("Generate", "antimanual") : __("Resume", "antimanual")}
                        </Button>
                    </div>
                </div>
            </form>
        </Section>
    );
}

type Props = React.HTMLProps<HTMLSpanElement> & {
    object: OutlineObjectType;
};

function LessonObjectTitle({ object, children, ...props }: Props) {
    const ref = useRef<HTMLSpanElement>(null);
    const { outline, setOutline, focusedLessonId, addLessonAfter, removeLesson, moveLessonFocus } = useDoc();
    const isLesson = outline?.lessons?.some((lesson) => lesson.id === object.id);

    const isLoading = object.loading;
    const url = object.url || "";
    const isEditable = !isLoading && !url;

    useEffect(() => {
        const el = ref.current;
        if (!el || !isLoading) return;

        const rect = el.getBoundingClientRect();
        const limit = window.innerHeight;

        if (rect.top < 0 || rect.bottom > limit) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [isLoading]);

    useEffect(() => {
        if (!ref.current || !focusedLessonId) return;

        if (focusedLessonId === object.id) {
            ref.current?.focus();
            setCaretAtOffset(ref.current, object.caretOffset || 0);
        }
    }, [focusedLessonId, object.id]);

    return (
        <span
            ref={ref}
            className={`atml-lesson-title-editable ${isLoading ? "loading" : ""}`}
            contentEditable={isEditable}
            onKeyDown={(e) => {
                if (!isEditable) return;

                if (e.key === "Enter") {
                    e.preventDefault();
                    addLessonAfter(object.id);
                } else if (e.key === "Backspace") {
                    if (object.title === "") {
                        e.preventDefault();
                        if (!isLesson) removeLesson(object.id);
                    }
                } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                    e.preventDefault();
                    moveLessonFocus(object.id, e.key === "ArrowDown" ? "next" : "prev");
                }
            }}
            onSelect={(e) => {
                object.caretOffset = getCaretOffset(e.currentTarget as HTMLElement);
            }}
            onInput={(e) => {
                object.title = e.currentTarget.textContent || "";
            }}
            onBlur={() => setOutline({ ...outline! })}
            {...props}
        >
            {url ? (
                <a href={url} target="_blank" className={css("td-none")}>
                    {children}
                </a>
            ) : (
                children || "\u200B"
            )}

            {isLoading && <Spinner className={css("w-3 h-3")} />}
        </span>
    );
}
